import { useState, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CalendarIcon, 
  User2Icon, 
  MoreHorizontalIcon, 
  PlusIcon,
  Pencil, 
  Trash2,
  Layers,
  Eye
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { TaskForm } from "./TaskForm";
import { PhaseForm } from "./PhaseForm";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { TaskDetailsDialog } from "@/components/tasks/TaskDetailsDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface ProjectKanbanBoardProps {
  projectId: string;
  phases?: any[];
  tasks?: any[];
  onTasksChange?: (tasks: any[]) => void;
  onPhasesChange?: (phases: any[]) => void;
  userRole?: string | null;
  isSharedView?: boolean;
  canEdit?: boolean;
  shareToken?: string;
  organizationId?: string;
  onDataRefresh?: () => void;
}

export const ProjectKanbanBoard = ({
  projectId,
  phases = [],
  tasks = [],
  onTasksChange,
  onPhasesChange,
  userRole,
  isSharedView = false,
  canEdit: canEditProp,
  shareToken,
  organizationId,
  onDataRefresh
}: ProjectKanbanBoardProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { session } = useAuth();
  const [editingPhase, setEditingPhase] = useState<any>(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'phase' | 'task', id: string, name: string } | null>(null);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const phaseIdForNewTask = useRef<string | null>(null);
  const [selectedPhaseFilter, setSelectedPhaseFilter] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskDetailsOpen, setIsTaskDetailsOpen] = useState(false);

  // Check if user can edit
  const canEdit = isSharedView ? (canEditProp ?? false) : (userRole === 'salesman');

  // Define status columns
  const statusColumns = [
    { id: 'todo', label: 'To Do' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'on_hold', label: 'On Hold' },
    { id: 'done', label: 'Completed' }
  ];

  // Sort phases by order_index
  const sortedPhases = [...phases].sort((a, b) => a.order_index - b.order_index);

  // Filter tasks by status and optionally by phase
  const getTasksByStatus = (status: string) => {
    return tasks
      .filter(task => task.status === status && 
                     (selectedPhaseFilter ? task.phase_id === selectedPhaseFilter : true))
      .sort((a, b) => a.order_index - b.order_index);
  };

  // Get phase name for a task
  const getPhaseNameForTask = (task: any) => {
    if (!task.phase_id) return null;
    const phase = phases.find(p => p.id === task.phase_id);
    return phase ? phase.name : null;
  };

  // Shared operation functions
  const callSharedOperation = async (action: string, data: any) => {
    const { data: response, error } = await supabase.functions.invoke('shared-project-operations', {
      body: {
        action,
        shareToken,
        ...data
      }
    });

    if (error) throw error;
    if (!response.success) throw new Error(response.error);
    
    return response;
  };

  // Regular operation functions
  const callRegularOperation = async (action: string, data: any) => {
    const orgId = currentOrganization?.id || organizationId;
    if (!orgId) throw new Error("Organization ID required");

    const { data: response, error } = await supabase.functions.invoke('project-tracking-operations', {
      body: {
        action,
        organizationId: orgId,
        ...data
      }
    });

    if (error) throw error;
    if (!response.success) throw new Error(response.error);
    
    return response;
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || !canEdit) return;

    const { source, destination, draggableId, type } = result;

    // If dragging a task
    if (type === 'task') {
      // Create a copy of the tasks array
      const updatedTasks = [...tasks];
      const taskIndex = updatedTasks.findIndex(t => t.id === draggableId);
      
      if (taskIndex === -1) return;
      
      const task = { ...updatedTasks[taskIndex] };
      
      // Update the task's status based on the destination column
      const newStatus = destination.droppableId;
      task.status = newStatus;
      
      // If status is changed to 'done', set completed_at
      if (newStatus === 'done' && !task.completed_at) {
        task.completed_at = new Date().toISOString();
      }
      
      // If status is changed from 'done', clear completed_at
      if (newStatus !== 'done') {
        task.completed_at = null;
      }
      
      // Remove the task from its source position
      updatedTasks.splice(taskIndex, 1);
      
      // Reinsert the task at its destination position
      updatedTasks.splice(destination.index, 0, task);
      
      try {
        // Update the task's status
        const updateData = {
          status: task.status,
          completed_at: task.completed_at
        };

        if (isSharedView) {
          await callSharedOperation('updateTask', {
            taskId: task.id,
            taskData: updateData
          });
        } else {
          await callRegularOperation('updateTask', {
            taskId: task.id,
            taskData: updateData
          });
        }
        
        // Update local state to reflect the change
        onTasksChange?.(updatedTasks);
        onDataRefresh?.();
      } catch (error) {
        console.error('Error updating task status:', error);
        toast({
          title: 'Error',
          description: 'Failed to update task status',
          variant: 'destructive'
        });
      }
    }
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete || !canEdit) return;
    
    try {
      if (itemToDelete.type === 'phase') {
        if (isSharedView) {
          await callSharedOperation('deletePhase', {
            phaseId: itemToDelete.id
          });
        } else {
          await callRegularOperation('deletePhase', {
            phaseId: itemToDelete.id
          });
        }
        
        toast({
          title: 'Phase Deleted',
          description: `${itemToDelete.name} has been deleted`
        });
      } else {
        if (isSharedView) {
          await callSharedOperation('deleteTask', {
            taskId: itemToDelete.id
          });
        } else {
          await callRegularOperation('deleteTask', {
            taskId: itemToDelete.id
          });
        }
        
        toast({
          title: 'Task Deleted',
          description: `${itemToDelete.name} has been deleted`
        });
      }
      
      onDataRefresh?.();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: 'Error',
        description: `Failed to delete ${itemToDelete.type}`,
        variant: 'destructive'
      });
    } finally {
      setIsConfirmDeleteOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAddTask = (phaseId: string | null) => {
    phaseIdForNewTask.current = phaseId;
    setIsAddTaskDialogOpen(true);
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const updateData = {
        status: 'done',
        completed_at: new Date().toISOString()
      };

      if (isSharedView) {
        await callSharedOperation('updateTask', {
          taskId,
          taskData: updateData
        });
      } else {
        await callRegularOperation('updateTask', {
          taskId,
          taskData: updateData
        });
      }
      
      // Update local state
      const updatedTasks = tasks.map(task => 
        task.id === taskId 
          ? { 
              ...task, 
              status: 'done', 
              completed_at: new Date().toISOString() 
            } 
          : task
      );
      
      onTasksChange?.(updatedTasks);
      onDataRefresh?.();
      
      toast({
        title: 'Task Completed',
        description: 'Task has been marked as completed'
      });
    } catch (error) {
      console.error('Error completing task:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete task',
        variant: 'destructive'
      });
    }
  };

  const handleTaskDetailsClick = (task: any) => {
    setSelectedTask(task);
    setIsTaskDetailsOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500 hover:bg-red-600';
      case 'medium':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'low':
        return 'bg-green-500 hover:bg-green-600';
      default:
        return 'bg-blue-500 hover:bg-blue-600';
    }
  };

  const handleTaskFormSuccess = () => {
    toast({
      title: "Success",
      description: "Task saved successfully"
    });
    setEditingTask(null);
    setIsAddTaskDialogOpen(false);
    onDataRefresh?.();
  };

  const handlePhaseFormSuccess = () => {
    toast({
      title: "Success",
      description: "Phase saved successfully"
    });
    setEditingPhase(null);
    onDataRefresh?.();
  };

  return (
    <div className="space-y-4">
      {/* Phase Management and Filtering */}
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-2">
          <h3 className="text-lg font-medium">Project Phases</h3>
          <div className="flex flex-wrap gap-2">
            <Badge 
              variant={selectedPhaseFilter === null ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setSelectedPhaseFilter(null)}
            >
              All Phases
            </Badge>
            {sortedPhases.map(phase => (
              <Badge 
                key={phase.id}
                variant={selectedPhaseFilter === phase.id ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => setSelectedPhaseFilter(phase.id === selectedPhaseFilter ? null : phase.id)}
              >
                {phase.name}
              </Badge>
            ))}
          </div>
        </div>
        
        {canEdit && (
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Phase
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Phase</DialogTitle>
              </DialogHeader>
              <PhaseForm 
                projectId={projectId} 
                onSuccess={handlePhaseFormSuccess}
                isSharedView={isSharedView}
                shareToken={shareToken}
                organizationId={organizationId}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status Columns */}
          {statusColumns.map(column => (
            <div key={column.id} className="bg-muted/50 rounded-md p-3 min-h-[50vh]">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="font-medium">{column.label}</h3>
                  <div className="text-xs text-muted-foreground">
                    {getTasksByStatus(column.id).length} tasks
                  </div>
                </div>
                
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddTask(null)}
                    className="h-8 w-8 p-0"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <Droppable droppableId={column.id} type="task">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-2 min-h-[100px]"
                  >
                    {getTasksByStatus(column.id).map((task, index) => (
                      <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={!canEdit}>
                        {(provided) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="mb-2 shadow-sm"
                          >
                            <CardHeader className="p-3 pb-1 flex flex-row justify-between items-start">
                              <h4 className="font-medium text-sm">{task.title}</h4>
                              {canEdit && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontalIcon className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setEditingTask(task)}>
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => {
                                        setItemToDelete({
                                          type: 'task',
                                          id: task.id,
                                          name: task.title
                                        });
                                        setIsConfirmDeleteOpen(true);
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </CardHeader>
                            <CardContent className="p-3 pt-1">
                              <div className="flex flex-wrap gap-1 mt-1 mb-2">
                                {task.priority && (
                                  <Badge
                                    variant="secondary"
                                    className={`text-white ${getPriorityColor(task.priority)}`}
                                  >
                                    {task.priority}
                                  </Badge>
                                )}
                                {getPhaseNameForTask(task) && (
                                  <Badge variant="outline" className="flex items-center text-xs">
                                    <Layers className="h-3 w-3 mr-1" />
                                    {getPhaseNameForTask(task)}
                                  </Badge>
                                )}
                              </div>
                              {task.due_date && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <CalendarIcon className="h-3 w-3 mr-1" />
                                  {format(parseISO(task.due_date), 'MMM d, yyyy')}
                                </div>
                              )}
                               {task.assignee?.full_name && (
                                 <div className="flex items-center text-xs text-muted-foreground mt-1">
                                   <User2Icon className="h-3 w-3 mr-1" />
                                   {task.assignee.full_name}
                                 </div>
                               )}
                               
                               {/* Action buttons */}
                               <div className="flex gap-2 mt-2">
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleTaskDetailsClick(task);
                                   }}
                                   className="flex-1 h-7 text-xs"
                                 >
                                   <Eye className="h-3 w-3 mr-1" />
                                   Ver Detalles
                                 </Button>
                                 {column.id !== 'done' && canEdit && (
                                   <Button 
                                     variant="default" 
                                     size="sm" 
                                     className="flex-1 h-7 text-xs"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       handleCompleteTask(task.id);
                                     }}
                                   >
                                     Completar
                                   </Button>
                                 )}
                               </div>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Edit Phase Dialog */}
      {editingPhase && (
        <Dialog open={true} onOpenChange={(open) => !open && setEditingPhase(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Phase</DialogTitle>
            </DialogHeader>
            <PhaseForm
              projectId={projectId}
              initialData={editingPhase}
              onSuccess={handlePhaseFormSuccess}
              isSharedView={isSharedView}
              shareToken={shareToken}
              organizationId={organizationId}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Task Dialog */}
      {editingTask && (
        <Dialog open={true} onOpenChange={(open) => !open && setEditingTask(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
            </DialogHeader>
            <TaskForm
              projectId={projectId}
              phases={phases}
              initialData={editingTask}
              onSuccess={handleTaskFormSuccess}
              isSharedView={isSharedView}
              shareToken={shareToken}
              organizationId={organizationId}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Add Task Dialog */}
      <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <TaskForm
            projectId={projectId}
            phases={phases}
            initialData={{ 
              phase_id: phaseIdForNewTask.current,
              status: 'todo'
            }}
            onSuccess={handleTaskFormSuccess}
            isSharedView={isSharedView}
            shareToken={shareToken}
            organizationId={organizationId}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={isConfirmDeleteOpen}
        onOpenChange={setIsConfirmDeleteOpen}
        title={`Delete ${itemToDelete?.type === 'phase' ? 'Phase' : 'Task'}`}
        description={`Are you sure you want to delete ${itemToDelete?.name}? This action cannot be undone.`}
        onConfirm={handleDeleteItem}
      />

      {/* Task Details Dialog */}
      <TaskDetailsDialog
        task={selectedTask}
        open={isTaskDetailsOpen}
        onOpenChange={setIsTaskDetailsOpen}
        onUpdate={onDataRefresh}
        userRole={userRole}
        isSharedView={isSharedView}
      />
    </div>
  );
};
